import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../application/provider";
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Table } from "reactstrap";
import { FileMinus, FileText, Printer } from "lucide-react";
import jsPDF from "jspdf";
import PagoProveedoresService from "../axios_services/pagoproveedores.service";
import ProveedorServiciosService from "../axios_services/proveedorservicios.service";
import Loader from "../components/Loader/Loader";
import { ButtonDelete, ButtonInsert, ButtonSearch, ButtonUpdate } from "../components/Buttons/Buttons";
import ModalConfirmar from "../components/Modal/ModalConfirmar";
import "./PagoProveedores.css";

const ESTADOS = [
    { id: 1, nombre: "Pendiente aprobación" },
    { id: 2, nombre: "Pendiente pago" },
    { id: 3, nombre: "Observado" },
    { id: 4, nombre: "Archivado" },
];

const FORM_INICIAL = {
    fecha_orden: new Date().toISOString().slice(0, 10),
    servicio_id: "",
    cantidad: 1,
    descripcion_servicio: "",
    precio_unitario: "",
    incluye_igv: false,
    archivo_factura: null,
};

function PagoProveedores() {
    const [state] = useContext(AppContext);
    const [registros, setRegistros] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [periodo, setPeriodo] = useState(() => new Date().toISOString().slice(0, 7));
    const [estado, setEstado] = useState("");
    const [modalFormulario, setModalFormulario] = useState(false);
    const [modalAccion, setModalAccion] = useState(false);
    const [registroSeleccionado, setRegistroSeleccionado] = useState(null);
    const [form, setForm] = useState(FORM_INICIAL);
    const [accion, setAccion] = useState("");
    const [observacionAccion, setObservacionAccion] = useState("");
    const [fechaPago, setFechaPago] = useState(new Date().toISOString().slice(0, 10));
    const [archivoPago, setArchivoPago] = useState(null);
    const [mensajeModal, setMensajeModal] = useState({ isOpen: false, titulo: "", mensaje: "", variante: "exito" });
    const [confirmacionModal, setConfirmacionModal] = useState({ isOpen: false, titulo: "", mensaje: "", textConfirmar: "Confirmar", variante: "exito", onConfirmar: null });
    const initialFetchDone = useRef(false);
    const facturaRef = useRef(null);
    const perfilId = Number(state.user?.perfil_id || state.perfil_id);
    const puedeRegistrar = [8, 26, 9].includes(perfilId);
    const puedeAprobar = [26, 9].includes(perfilId);

    const formatoSoles = (monto) => new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" }).format(Number(monto || 0));

    const formatoFecha = (fecha) => {
        if (!fecha) return "-";
        const date = new Date(fecha);
        if (isNaN(date.getTime())) return "-";
        return date.toLocaleDateString("es-PE", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "UTC" });
    };

    const mostrarMensaje = useCallback((titulo, mensaje, variante = "exito") => {
        setMensajeModal({ isOpen: true, titulo, mensaje, variante });
    }, []);

    const obtenerMensajeError = (error, fallback) => {
        const errores = error.response?.data?.errors;
        if (errores) return Object.values(errores).flat().join("\n");
        return error.response?.data?.message || fallback;
    };

    const totalOrdenes = useMemo(() => registros.reduce((acc, item) => acc + Number(item.total || 0), 0), [registros]);

    const calcularImportes = (precio, cantidad, incluyeIgv) => {
        const total = Number(precio || 0) * Number(cantidad || 1);
        if (incluyeIgv) {
            const subtotal = total / 1.18;
            return { subtotal, igv: total - subtotal, total };
        }
        return { subtotal: total, igv: 0, total };
    };

    const cargarServicios = useCallback(async () => {
        const response = await ProveedorServiciosService.getServiciosProveedor();
        setServicios(response.data || []);
    }, []);

    const cargarRegistros = useCallback(async () => {
        setLoading(true);
        try {
            const response = await PagoProveedoresService.getPagoProveedores(periodo, estado);
            setRegistros(response.data || []);
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al cargar pagos de proveedores"), "peligro");
            setRegistros([]);
        } finally {
            setLoading(false);
        }
    }, [periodo, estado, mostrarMensaje]);

    useEffect(() => {
        if (!initialFetchDone.current) {
            cargarServicios();
            cargarRegistros();
            initialFetchDone.current = true;
        }
    }, [cargarServicios, cargarRegistros]);

    const abrirNuevo = () => {
        setRegistroSeleccionado(null);
        setForm({ ...FORM_INICIAL, fecha_orden: new Date().toISOString().slice(0, 10) });
        setModalFormulario(true);
    };

    const abrirEditar = (registro) => {
        setRegistroSeleccionado(registro);
        setForm({
            fecha_orden: registro.fecha_orden ? String(registro.fecha_orden).slice(0, 10) : new Date().toISOString().slice(0, 10),
            servicio_id: registro.servicio_id || "",
            cantidad: registro.cantidad || 1,
            descripcion_servicio: registro.descripcion_servicio || "",
            precio_unitario: registro.precio_unitario || "",
            incluye_igv: Number(registro.incluye_igv) === 1,
            archivo_factura: null,
        });
        setModalFormulario(true);
    };

    const cerrarFormulario = () => {
        setModalFormulario(false);
        setRegistroSeleccionado(null);
        setForm(FORM_INICIAL);
    };

    const handleFormChange = (e) => {
        const { name, value, files, checked, type } = e.target;

        if (name === "servicio_id") {
            const servicio = servicios.find(item => Number(item.servicio_id) === Number(value));
            setForm(prev => ({
                ...prev,
                servicio_id: value,
                descripcion_servicio: servicio?.descripcion_servicio || "",
                precio_unitario: servicio?.precio || "",
                incluye_igv: Number(servicio?.incluye_igv) === 1,
            }));
            return;
        }

        setForm(prev => ({ ...prev, [name]: files ? files[0] : type === "checkbox" ? checked : value }));
    };

    const guardarRegistro = async () => {
        if (!form.fecha_orden || !form.servicio_id || !form.descripcion_servicio || !form.precio_unitario) {
            mostrarMensaje("Validación", "Complete los campos obligatorios", "peligro");
            return;
        }

        if (!registroSeleccionado && !form.archivo_factura) {
            mostrarMensaje("Validación", "Adjunte la factura del servicio", "peligro");
            return;
        }

        setLoading(true);
        try {
            await PagoProveedoresService.guardarPagoProveedor(form, registroSeleccionado?.pago_proveedor_id);
            cerrarFormulario();
            await cargarRegistros();
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al guardar la orden de servicio"), "peligro");
        } finally {
            setLoading(false);
        }
    };

    const abrirAccion = (tipo, registro) => {
        if (tipo === "aprobar") {
            setConfirmacionModal({
                isOpen: true,
                titulo: "Aprobar orden",
                mensaje: `¿Está seguro de aprobar la orden ${registro.numero_orden_servicio}?`,
                textConfirmar: "Aprobar",
                variante: "exito",
                onConfirmar: async () => {
                    setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                    setLoading(true);
                    try {
                        await PagoProveedoresService.aprobarPagoProveedor(registro.pago_proveedor_id);
                        await cargarRegistros();
                    } catch (error) {
                        mostrarMensaje("Error", obtenerMensajeError(error, "Error al aprobar la orden"), "peligro");
                    } finally {
                        setLoading(false);
                    }
                },
            });
            return;
        }

        setAccion(tipo);
        setRegistroSeleccionado(registro);
        setObservacionAccion("");
        setFechaPago(new Date().toISOString().slice(0, 10));
        setArchivoPago(null);
        setModalAccion(true);
    };

    const cerrarAccion = () => {
        setModalAccion(false);
        setRegistroSeleccionado(null);
        setAccion("");
    };

    const ejecutarAccion = async () => {
        if (!registroSeleccionado) return;

        if (accion === "observar" && !observacionAccion.trim()) {
            mostrarMensaje("Validación", "Ingrese una observación", "peligro");
            return;
        }

        if (accion === "pagar" && !archivoPago) {
            mostrarMensaje("Validación", "Adjunte el comprobante de pago", "peligro");
            return;
        }

        setLoading(true);
        try {
            if (accion === "aprobar") {
                await PagoProveedoresService.aprobarPagoProveedor(registroSeleccionado.pago_proveedor_id, observacionAccion);
            }

            if (accion === "observar") {
                await PagoProveedoresService.observarPagoProveedor(registroSeleccionado.pago_proveedor_id, observacionAccion);
            }

            if (accion === "pagar") {
                await PagoProveedoresService.marcarPagoRealizado(registroSeleccionado.pago_proveedor_id, fechaPago, archivoPago);
            }

            cerrarAccion();
            await cargarRegistros();
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al procesar la acción"), "peligro");
        } finally {
            setLoading(false);
        }
    };

    const anularRegistro = (registro) => {
        setConfirmacionModal({
            isOpen: true,
            titulo: "Anular orden",
            mensaje: `¿Está seguro de anular la orden ${registro.numero_orden_servicio}?`,
            textConfirmar: "Anular",
            variante: "peligro",
            onConfirmar: async () => {
                setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                setLoading(true);
                try {
                    await PagoProveedoresService.anularPagoProveedor(registro.pago_proveedor_id);
                    await cargarRegistros();
                } catch (error) {
                    mostrarMensaje("Error", obtenerMensajeError(error, "Error al anular la orden"), "peligro");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const abrirArchivo = (url, mensaje) => {
        if (!url) {
            mostrarMensaje("Aviso", mensaje, "peligro");
            return;
        }
        window.open(url, "_blank");
    };

    const generarPdfOrden = (registro, imprimir = false) => {
        const doc = new jsPDF("p", "pt", "a4");
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 48;
        const right = pageWidth - margin;
        const proveedorDocumento = registro.tipo_documento || (String(registro.documento || "").length === 8 ? "DNI" : "RUC");
        const tieneIgv = Number(registro.igv || 0) > 0;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text("Orden de Servicio", margin, 58);
        doc.setFontSize(10);
        doc.text(`No. Orden de Servicio: ${registro.numero_orden_servicio}`, right, 58, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.text(`Fecha: ${formatoFecha(registro.fecha_orden)}`, right, 76, { align: "right" });

        doc.setFont("helvetica", "bold");
        doc.text("Cliente", margin, 116);
        doc.setFont("helvetica", "normal");
        doc.text("Razón social: MEC BUSINESS PARTNER", margin, 134);
        doc.text("RUC: 20612796484", margin, 152);
        doc.text("Dirección: Pje. canal municipal Nro. 153 Mz. 0 Lt. 0 Piso 4", margin, 170);

        doc.setFont("helvetica", "bold");
        doc.text("Proveedor", margin, 206);
        doc.setFont("helvetica", "normal");
        doc.text(`Nombres y Apellidos/Razón social: ${registro.nombre_razon_social || "-"}`, margin, 224);
        doc.text(`${proveedorDocumento}: ${registro.documento || "-"}`, margin, 242);

        const tableTop = 278;
        doc.setFont("helvetica", "bold");
        doc.rect(margin, tableTop, pageWidth - margin * 2, 28);
        doc.text("Cantidad", margin + 14, tableTop + 18);
        doc.text("Descripción del Servicio", margin + 100, tableTop + 18);
        doc.text("Precio Unitario", right - 92, tableTop + 18);

        doc.setFont("helvetica", "normal");
        const descripcion = doc.splitTextToSize(registro.descripcion_servicio || "-", 292);
        const rowHeight = Math.max(56, descripcion.length * 14 + 22);
        doc.rect(margin, tableTop + 28, pageWidth - margin * 2, rowHeight);
        doc.text(String(registro.cantidad || 1), margin + 32, tableTop + 55);
        doc.text(descripcion, margin + 100, tableTop + 55);
        doc.text(formatoSoles(registro.precio_unitario), right - 18, tableTop + 55, { align: "right" });

        const totalsTop = tableTop + 28 + rowHeight;
        doc.rect(right - 190, totalsTop, 190, 78);
        doc.text("Subtotal", right - 178, totalsTop + 20);
        doc.text(formatoSoles(registro.subtotal), right - 18, totalsTop + 20, { align: "right" });
        doc.text(tieneIgv ? "I.G.V. (18%)" : "I.G.V.", right - 178, totalsTop + 44);
        doc.text(formatoSoles(registro.igv), right - 18, totalsTop + 44, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text("Total", right - 178, totalsTop + 68);
        doc.text(formatoSoles(registro.total), right - 18, totalsTop + 68, { align: "right" });

        const obsTop = totalsTop + 122;
        doc.setFont("helvetica", "bold");
        doc.text("Observaciones", margin, obsTop);
        doc.setFont("helvetica", "normal");
        doc.text("Realizar el pago en:", margin, obsTop + 22);
        doc.text(`Cuenta ${registro.tipo_banco || "-"}: ${registro.numero_cuenta || "-"}`, margin, obsTop + 44);
        doc.text(`Cuenta interbancaria: ${registro.cuenta_interbancaria || "-"}`, margin, obsTop + 66);

        const firmaTop = 700;
        const estadoProceso = Number(registro.estado_proceso);

        const drawSignature = (base64, nombre, cargo, x) => {
            if (base64) {
                try {
                    const format = base64.includes("image/jpeg") || base64.includes("image/jpg") ? "JPEG" : "PNG";
                    doc.addImage(base64, format, x - 50, firmaTop - 50, 100, 40);
                } catch (e) {
                    console.error("Error al agregar firma:", e);
                }
            }
            doc.line(x - 102, firmaTop, x + 103, firmaTop);
            doc.setFont("helvetica", "bold");
            doc.text(nombre || "Sin nombre", x, firmaTop + 18, { align: "center" });
            doc.setFont("helvetica", "normal");
            doc.text(cargo || "Sin cargo", x, firmaTop + 34, { align: "center" });
        };

        // Si la orden ya está aprobada (estado 2: Pendiente pago) o archivada (estado 4: Archivado), mostramos ambas firmas digitales (26 y 9)
        const mostrarFirmas = [2, 4].includes(estadoProceso);

        drawSignature(
            mostrarFirmas ? registro.firma_perfil_26_base64 : null,
            registro.nombre_perfil_26 || "Sub Gerencia de Administración",
            registro.cargo_perfil_26 || "Sub Gerente de Administración",
            margin + 102
        );

        drawSignature(
            mostrarFirmas ? registro.firma_perfil_9_base64 : null,
            registro.nombre_perfil_9 || "Gerencia General",
            registro.cargo_perfil_9 || "Gerente General",
            right - 102
        );

        if (imprimir) {
            doc.autoPrint();
        }

        window.open(doc.output("bloburl"), "_blank");
    };

    const puedeEditar = (registro) => puedeRegistrar && [1, 2, 3].includes(Number(registro.estado_proceso));
    const puedePagar = (registro) => puedeRegistrar && Number(registro.estado_proceso) === 2;
    const puedeAnular = (registro) => puedeRegistrar && Number(registro.estado_proceso) !== 4;
    const puedeImprimir = (registro) => [2, 4].includes(Number(registro.estado_proceso));
    const claseFilaEstado = (estadoProceso) => {
        if (Number(estadoProceso) === 3) return "fila-observada";
        if (Number(estadoProceso) === 4) return "fila-archivada";
        return "";
    };
    const importesForm = calcularImportes(form.precio_unitario, form.cantidad, form.incluye_igv);

    return (
        <div className="caja-flujo-container pago-proveedores-scope">
            {loading && <Loader />}

            <div className="flex items-center justify-between mb-4">
                <h1>PAGO DE PROVEEDORES</h1>
            </div>

            <div className="filtros">
                <div className="campo-fecha">
                    <Label>Periodo:</Label>
                    <Input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
                </div>
                <div className="campo-fecha">
                    <Label>Estado:</Label>
                    <Input type="select" value={estado} onChange={(e) => setEstado(e.target.value)}>
                        <option value="">Todos los estados</option>
                        {ESTADOS.map(item => <option key={item.id} value={item.id}>{item.nombre}</option>)}
                    </Input>
                </div>
            </div>

            <ButtonSearch onClick={cargarRegistros} isLoading={loading} />

            <div className="modulo-toolbar">
                {puedeRegistrar && <ButtonInsert onClick={abrirNuevo} />}
            </div>

            <div className="numero-propuestas">
                <p>N° órdenes: {registros.length}</p>
                <p>Total: <span>{formatoSoles(totalOrdenes)}</span></p>
            </div>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>N° ORDEN</th>
                            <th>FECHA</th>
                            <th>PROVEEDOR</th>
                            <th>DOCUMENTO</th>
                            <th>SERVICIO</th>
                            <th>TOTAL</th>
                            <th>ESTADO</th>
                            <th>OBSERVACIÓN</th>
                            <th>FACTURA</th>
                            <th>PAGO</th>
                            <th>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        {registros.length === 0 ? (
                            <tr><td colSpan="11" className="sin-data">No hay pagos de proveedores para el periodo seleccionado</td></tr>
                        ) : registros.map(registro => (
                            <tr key={registro.pago_proveedor_id} className={claseFilaEstado(registro.estado_proceso)}>
                                <td>{registro.numero_orden_servicio}</td>
                                <td>{formatoFecha(registro.fecha_orden)}</td>
                                <td>{registro.nombre_razon_social}</td>
                                <td>{registro.tipo_documento}: {registro.documento}</td>
                                <td className="descripcion-col">{registro.descripcion_servicio}</td>
                                <td>{formatoSoles(registro.total)}</td>
                                <td className="estado-proceso">{registro.estado_nombre}</td>
                                <td className="observacion-col">{registro.observacion_aprobacion || "-"}</td>
                                <td className="documento-cell">
                                    <button type="button" className={`documento-icono ${!registro.archivo_factura_url ? "sin-archivo" : ""}`} onClick={() => abrirArchivo(registro.archivo_factura_url, "No hay factura adjunta")} title="Ver factura">
                                        <FileMinus size={20} style={{ color: registro.archivo_factura_url ? "#3cb167" : "#f87171" }} />
                                    </button>
                                </td>
                                <td className="documento-cell">
                                    <button type="button" className={`documento-icono ${!registro.archivo_pago_url ? "sin-archivo" : ""}`} onClick={() => abrirArchivo(registro.archivo_pago_url, "No hay comprobante de pago")} title="Ver comprobante">
                                        <FileMinus size={20} style={{ color: registro.archivo_pago_url ? "#3cb167" : "#f87171" }} />
                                    </button>
                                </td>
                                <td>
                                    <div className="acciones-container">
                                        <button type="button" className="boton-pdf" onClick={() => generarPdfOrden(registro)} title="Ver orden PDF">
                                            <FileText size={16} />
                                        </button>
                                        {puedeImprimir(registro) && (
                                            <button type="button" className="boton-imprimir" onClick={() => generarPdfOrden(registro, true)} title="Imprimir">
                                                <Printer size={16} />
                                            </button>
                                        )}
                                        {puedeEditar(registro) && <ButtonUpdate onClick={() => abrirEditar(registro)} />}
                                        {puedeAprobar && Number(registro.estado_proceso) === 1 && (
                                            <>
                                                <button type="button" className="boton-aprobar" onClick={() => abrirAccion("aprobar", registro)}>Aprobar</button>
                                                <button type="button" className="boton-observar" onClick={() => abrirAccion("observar", registro)}>Observar</button>
                                            </>
                                        )}
                                        {puedePagar(registro) && <button type="button" className="boton-pagar" onClick={() => abrirAccion("pagar", registro)}>Pagar</button>}
                                        {puedeAnular(registro) && <ButtonDelete onClick={() => anularRegistro(registro)} />}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <Modal isOpen={modalFormulario} className="custom-modal" backdrop="static" size="lg" fade={false}>
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>{registroSeleccionado ? "EDITAR ORDEN DE SERVICIO" : "REGISTRAR ORDEN DE SERVICIO"}</h1>
                        <Button className="close-btn" onClick={cerrarFormulario}>X</Button>
                    </div>
                </ModalHeader>
                <div className="modal-body-footer">
                    <ModalBody className="modal-body">
                        <Form className="fila" onSubmit={(e) => e.preventDefault()}>
                            <FormGroup>
                                <Label>Fecha:</Label>
                                <Input className="form-control" type="date" name="fecha_orden" value={form.fecha_orden} onChange={handleFormChange} />
                            </FormGroup>
                            <FormGroup className="full-width">
                                <Label>Servicio:</Label>
                                <Input className="form-control" type="select" name="servicio_id" value={form.servicio_id} onChange={handleFormChange}>
                                    <option value="">Seleccione</option>
                                    {servicios.map(servicio => (
                                        <option key={servicio.servicio_id} value={servicio.servicio_id}>{servicio.nombre_razon_social} - {servicio.descripcion_servicio}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label>Cantidad:</Label>
                                <Input className="form-control" type="number" min="1" name="cantidad" value={form.cantidad} onChange={handleFormChange} />
                            </FormGroup>
                            <FormGroup>
                                <Label>Precio unitario:</Label>
                                <div className="input-group custom-input-group">
                                    <span className="input-group-text custom-symbol">S/.</span>
                                    <Input className="form-control" type="number" step="0.01" name="precio_unitario" value={form.precio_unitario} onChange={handleFormChange} />
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <Label>Incluye IGV:</Label>
                                <div>
                                    <Input type="checkbox" name="incluye_igv" checked={form.incluye_igv} onChange={handleFormChange} />
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <Label>Total:</Label>
                                <Input className="form-control" value={formatoSoles(importesForm.total)} disabled />
                            </FormGroup>
                            <FormGroup className="full-width">
                                <Label>Descripción del servicio:</Label>
                                <Input className="form-control" type="textarea" name="descripcion_servicio" value={form.descripcion_servicio} onChange={handleFormChange} />
                            </FormGroup>
                            <FormGroup className="mb-4">
                                <Label>Factura del servicio (PDF, IMG):</Label>
                                <div className="cap-upload-zone">
                                    <span className="cap-upload-btn" onClick={() => facturaRef.current?.click()}>
                                        {form.archivo_factura ? form.archivo_factura.name : 'Seleccionar archivo (.pdf, .jpg, .png)'}
                                    </span>
                                    <input
                                        ref={facturaRef}
                                        type="file"
                                        name="archivo_factura"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        style={{ display: 'none' }}
                                        onChange={handleFormChange}
                                    />
                                </div>
                                {form.archivo_factura && (
                                    <small>
                                        <span
                                            style={{ cursor: 'pointer', color: '#f87171', fontSize: '10px' }}
                                            onClick={() => {
                                                setForm(prev => ({ ...prev, archivo_factura: null }));
                                                if (facturaRef.current) facturaRef.current.value = '';
                                            }}
                                        >
                                            ✕ Quitar selección
                                        </span>
                                    </small>
                                )}
                                {registroSeleccionado?.archivo_factura_url && !form.archivo_factura && (
                                    <small className="text-success mt-1 d-block">
                                        ✓ Ya existe una factura subida.{' '}
                                        <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.open(registroSeleccionado.archivo_factura_url, '_blank')}>Ver documento</span>
                                    </small>
                                )}
                            </FormGroup>
                            <FormGroup>
                                <div className="button-container">
                                    <Button className="button-custom" onClick={() => setConfirmacionModal({
                                        isOpen: true,
                                        titulo: "Confirmar registro",
                                        mensaje: registroSeleccionado ? "¿Está seguro de actualizar esta orden?" : "¿Está seguro de guardar esta orden?",
                                        textConfirmar: "Guardar",
                                        variante: "exito",
                                        onConfirmar: async () => {
                                            setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                                            await guardarRegistro();
                                        },
                                    })}>
                                        GUARDAR
                                    </Button>
                                </div>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                </div>
            </Modal>

            <Modal isOpen={modalAccion} className="custom-modal" backdrop="static" fade={false}>
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>{accion === "aprobar" ? "APROBAR ORDEN" : accion === "observar" ? "OBSERVAR ORDEN" : "REGISTRAR PAGO"}</h1>
                        <Button className="close-btn" onClick={cerrarAccion}>X</Button>
                    </div>
                </ModalHeader>
                <div className="modal-body-footer">
                    <ModalBody className="modal-body">
                        <Form className="fila" onSubmit={(e) => e.preventDefault()}>
                            {accion === "pagar" && (
                                <>
                                    <FormGroup>
                                        <Label>Fecha de pago:</Label>
                                        <Input className="form-control" type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Comprobante de pago:</Label>
                                        <Input className="form-control" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setArchivoPago(e.target.files[0])} />
                                    </FormGroup>
                                </>
                            )}
                            {accion === "observar" && (
                                <FormGroup className="full-width">
                                    <Label>Motivo de observación:</Label>
                                    <Input className="form-control" type="textarea" value={observacionAccion} onChange={(e) => setObservacionAccion(e.target.value)} />
                                </FormGroup>
                            )}
                            <FormGroup>
                                <div className="button-container">
                                    <Button className="button-custom" onClick={() => setConfirmacionModal({
                                        isOpen: true,
                                        titulo: "Confirmar acción",
                                        mensaje: "¿Está seguro de realizar esta acción?",
                                        textConfirmar: "Confirmar",
                                        variante: "exito",
                                        onConfirmar: async () => {
                                            setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
                                            await ejecutarAccion();
                                        },
                                    })}>
                                        CONFIRMAR
                                    </Button>
                                </div>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                </div>
            </Modal>

            <ModalConfirmar
                isOpen={mensajeModal.isOpen}
                titulo={mensajeModal.titulo}
                mensaje={mensajeModal.mensaje}
                textConfirmar="Aceptar"
                textCancelar="Cerrar"
                variante={mensajeModal.variante}
                onConfirmar={() => setMensajeModal(prev => ({ ...prev, isOpen: false }))}
                onCancelar={() => setMensajeModal(prev => ({ ...prev, isOpen: false }))}
            />
            <ModalConfirmar
                isOpen={confirmacionModal.isOpen}
                titulo={confirmacionModal.titulo}
                mensaje={confirmacionModal.mensaje}
                textConfirmar={confirmacionModal.textConfirmar}
                textCancelar="Cancelar"
                variante={confirmacionModal.variante}
                onConfirmar={() => confirmacionModal.onConfirmar && confirmacionModal.onConfirmar()}
                onCancelar={() => setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }))}
            />
        </div>
    );
}

export default PagoProveedores;
